import { Helmet } from "react-helmet-async";

const SITE_URL = "https://mart.trendbayds.com";
const DEFAULT_IMAGE = "https://mart.trendbayds.com/og-default.png";
const SITE_NAME = "TrendBay Mart";
const TWITTER_HANDLE = "@trendbayds";

/**
 * SEO component — drop into any page.
 *
 * Props:
 *   title        – page title (do NOT include site name, it's appended automatically)
 *   description  – meta description (max ~155 chars)
 *   image        – absolute URL for OG/Twitter image
 *   path         – URL path e.g. "/product/nike-air-force"  (canonical)
 *   type         – OG type: "website" | "product" (default "website")
 *   product      – product price data: { price, currency, availability, condition }
 *   jsonLd       – array of JSON-LD objects (or single object)
 *   noIndex      – set true to add noindex (auth pages etc.)
 */
export default function SEO({
  title,
  description,
  image,
  path = "",
  type = "website",
  product,
  jsonLd,
  noIndex = false,
}) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Nigeria's Daily Marketplace`;
  const metaDesc = description ||
    "Shop fresh farm produce, daily essentials, and authentic pre-owned items on TrendBay Mart. Fast delivery across Nigeria.";
  const canonicalUrl = `${SITE_URL}${path}`;
  const ogImage = image || DEFAULT_IMAGE;

  const schemas = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <link rel="canonical" href={canonicalUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* ── Open Graph ─────────────────────────────────── */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:locale" content="en_NG" />

      {/* Product-specific OG (Facebook/Pinterest product pins) */}
      {product && (
        <>
          <meta property="product:price:amount" content={String(product.price)} />
          <meta property="product:price:currency" content={product.currency || "NGN"} />
          {product.availability && (
            <meta property="product:availability" content={product.availability} />
          )}
          {product.condition && (
            <meta property="product:condition" content={product.condition} />
          )}
        </>
      )}

      {/* ── Twitter Card ────────────────────────────────── */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={ogImage} />

      {/* ── JSON-LD Structured Data ─────────────────────── */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
